import React, { FC, HTMLAttributes } from 'react'
import { CaptionProps, DayPicker as ReactDayPicker } from 'react-day-picker'
import 'react-day-picker/dist/style.css'
import { BoraTheme, DayPickerProps } from '@/root/shared-types'
import { useConfig, useCustomRouter } from '@/root/shared-hooks'
import { CustomNavbar } from '@/root/components/shared'
import { Head as TuuleliinidHead } from '@/root/components/shared/DayPicker/tuuleliinid/CustomWeekBar'
import { Head as KihnuHead } from '@/root/components/shared/DayPicker/kihnu/CustomWeekBar'
import { Head as SarkHead } from '@/root/components/shared/DayPicker/sark/CustomWeekBar'
import { CustomNavbar as SarkNavbar } from '@/root/components/shared/DayPicker/sark/CustomNavbar'
import tuuleliinidStyles from '@/root/components/shared/DayPicker/tuuleliinid/styles.module.css'
import kihnuStyles from '@/root/components/shared/DayPicker/kihnu/styles.module.css'
import sarkStyles from '@/root/components/shared/DayPicker/sark/styles.module.css'
import { enUS, et } from 'date-fns/locale'
import * as R from 'ramda'

type DayPickerStyles = Record<string, string>

type DayPickerThemeConfig = {
  Head: () => React.JSX.Element
  styles: DayPickerStyles
  rootClassName?: string
  rdpStyles?: React.ComponentProps<typeof ReactDayPicker>['styles']
  /** Typography of the month label. Left out by a theme keeps the navbar's own default. */
  captionClassName?: string
  /** Month label and arrows. Left out by a theme uses the shared navbar. */
  Navbar?: typeof CustomNavbar
}

const DayPicker: FC<HTMLAttributes<HTMLSpanElement> & DayPickerProps> = ({
  selectedDate,
  defaultMonth,
  onDayClick,
  isDayDisabled,
  className,
  children,
  isCustomStyle = false,
}) => {
  const { theme } = useConfig()
  const { locale } = useCustomRouter()

  const DAY_PICKER_THEME_MAP: Record<BoraTheme, DayPickerThemeConfig> = {
    tuuleliinid: { Head: TuuleliinidHead, styles: tuuleliinidStyles },
    kihnu: {
      Head: KihnuHead,
      styles: kihnuStyles,
      rootClassName: 'w-full pt-1 text-[15px]',
      captionClassName: 'text-primary-base text-[15px] font-bold tracking-[0.04em]',
      // Stretch the grid to fill the card: columns widen, day circles stay centred at --rdp-cell-size.
      rdpStyles: {
        months: { width: '100%' },
        month: { width: '100%' },
        table: { width: '100%', maxWidth: 'none', tableLayout: 'fixed' },
      },
    },
    // Penpot "UI components / Calendar"
    sark: { Head: SarkHead, styles: sarkStyles, Navbar: SarkNavbar },
    // The TL booking flow, so the TL calendar
    trial: { Head: TuuleliinidHead, styles: tuuleliinidStyles },
  }

  const themeConfig =
    DAY_PICKER_THEME_MAP[theme as keyof typeof DAY_PICKER_THEME_MAP] ?? DAY_PICKER_THEME_MAP.tuuleliinid

  const { Head: HeadComponent, styles, rootClassName, rdpStyles, captionClassName, Navbar = CustomNavbar } = themeConfig

  const Caption = React.useMemo(() => {
    const DayPickerCaption = (captionProps: CaptionProps) => (
      <Navbar {...captionProps} captionClassName={captionClassName} />
    )
    DayPickerCaption.displayName = 'DayPickerCaption'
    return DayPickerCaption
  }, [Navbar, captionClassName])

  const dateFnsLocale = getDateFnsLocale(locale)

  const defaultClasses = isCustomStyle
    ? 'flex w-screen justify-center p-2 font-normal xxs:w-95 xxs:p-4 xxs:pt-3 lg:w-93 lg:ps-2 lg:pt-4'
    : (rootClassName ?? 'border-neutral-normal-lighter flex h-86 justify-center rounded-lg border pt-5 text-lg')

  return (
    <span data-testid="departureForm" className={className}>
      {children}

      <ReactDayPicker
        data-testid="calendar-wrapper"
        className={`${defaultClasses} m-0!`}
        styles={rdpStyles}
        selected={selectedDate}
        onDayClick={onDayClick}
        disabled={isDayDisabled}
        weekStartsOn={1}
        modifiers={{
          allDays: R.T(),
        }}
        modifiersClassNames={{
          selected: styles.rdpDaySelected,
          today: styles.rdpDayToday,
          allDays: styles.rdpDayCommon,
        }}
        defaultMonth={defaultMonth ?? selectedDate}
        components={{
          Caption,
          Head: HeadComponent,
        }}
        locale={dateFnsLocale}
      />
    </span>
  )
}

const getDateFnsLocale = (locale: string) => {
  switch (locale) {
    case 'et':
      return et
    default:
      return enUS
  }
}

export default DayPicker
